import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import ServiceCard from '@/components/cards/ServiceCard';
import DoctorCard from '@/components/cards/DoctorCard';
import InsuranceCarousel from '@/components/cards/InsuranceCarousel';
import BioModal from '@/components/common/BioModal';
import GoogleReviewsWidget from '@/components/common/GoogleReviewsWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default slides as fallback
const defaultHeroSlides = [
  {
    title: 'Welcome to Fort Worth Dental',
    subtitle: 'Your Family Dentist in Fort Worth',
    link: '/our-team',
    image: '/images/services/banner-website-img-1.webp',
    imageFallback: '/images/services/banner-website-img-1.png',
    isTeamPhoto: true,
    width: 800,
    height: 600
  },
  {
    title: 'First Fit Veneers',
    subtitle: 'A Brand-New Smile in Just 2 Hours',
    link: '/first-fit-veneers',
    image: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=800&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=800&q=70&fit=crop',
    width: 800,
    height: 600
  },
  {
    title: 'Dental Implants',
    subtitle: 'The Modern Standard of care for Missing Teeth',
    link: '/dental-implants',
    image: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=800&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=800&q=70&fit=crop',
    width: 800,
    height: 600
  },
  {
    title: 'Invisalign® Braces',
    subtitle: 'Smile with Confidence',
    link: '/invisalign',
    image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=800&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=800&q=70&fit=crop',
    width: 800,
    height: 600
  },
  {
    title: 'Dazzling Smiles',
    subtitle: 'Call Today Schedule a teeth Whitening',
    link: '/cosmetic-dentistry',
    image: 'https://images.unsplash.com/photo-1654373535457-383a0a4d00f9?w=800&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1654373535457-383a0a4d00f9?w=800&q=70&fit=crop',
    width: 800,
    height: 600
  }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);

  // Fetch slides from CMS
  useEffect(() => {
    const ffvSlide = {
      title: 'First Fit Veneers',
      subtitle: 'A Brand-New Smile in Just 2 Hours',
      link: '/first-fit-veneers',
      image: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=800&q=70&fit=crop&fm=webp',
      imageFallback: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=800&q=70&fit=crop',
      width: 800,
      height: 600
    };

    const fetchSlides = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/content/slides`);
        if (response.ok) {
          const data = await response.json();
          if (data.slides && data.slides.length > 0) {
            const cmsSlides = data.slides.map(slide => ({
              title: slide.title,
              subtitle: slide.subtitle,
              link: slide.link,
              image: slide.image,
              imageFallback: slide.imageFallback || slide.image,
              isTeamPhoto: slide.isTeamPhoto || false,
              width: slide.width || 800,
              height: slide.height || 600
            }));
            // Ensure FFV slide is always included
            const hasFFV = cmsSlides.some(s => s.link === '/first-fit-veneers');
            if (!hasFFV) {
              cmsSlides.splice(1, 0, ffvSlide);
            }
            setHeroSlides(cmsSlides);
          }
        }
      } catch (error) {
        console.log('Using default slides');
      }
    };
    fetchSlides();
  }, []);

  const welcomeOffers = [
    {
      title: 'Dental Implants Free Consult with X-Rays*',
      description: 'Fort Worth Dental is now offering FREE CONSULTATION plus X-Rays for new dental implant patients',
      image: '/images/services/frame-1288-3.png',
      link: '/dental-implants-free-consult-with-x-rays'
    },
    {
      title: 'Invisalign® - Free Consultation',
      description: 'Fort Worth Dental is now offering FREE CONSULTATION for Invisalign®.',
      image: '/images/services/frame-1288-2.png',
      link: '/invisalign-free-consultation'
    },
    {
      title: '$199 Healthy Cleaning, Exam and set of X-Rays*',
      description: 'What is the secret for avoiding costly and painful dental work? The secret is preventative dental practices.',
      image: '/images/services/frame-1288.png',
      link: '/179-healthy-cleaning-exam-and-set-of-x-rays'
    },
    {
      title: '$99 Emergency Exam and X-Ray Affordable Dental Services',
      description: 'The best dental problem is the one that you never had! Get emergency care when you need it.',
      image: '/images/services/frame-1288-1.png',
      link: '/99-emergency-exam-and-x-ray-affordable-dental-services'
    }
  ];

  const doctors = [
    {
      name: 'Dr. Arthur Mund',
      title: 'D.D.S., Founder',
      bio: 'Dr. Arthur J. Mund brings extensive training and over 40 years of cosmetic and implant dentistry experience to the North Texas area.',
      image: '/images/team/dr-mund.jpg',
      specialties: ['Dental Implants', 'Oral Implantology', 'Cosmetic Dentistry'],
      fullBio: "Dr. Arthur J. Mund brings extensive training and over 40 years of cosmetic and implant dentistry experience to the North Texas area. Graduating from the University of Texas Dental School at Houston in 1982, Dr. Mund has become a leader in his field. He is board certified by the American Board of Oral Implantology and is recognized for his expertise in implant procedures, making him a sought-after dentist for complex cases referred by other professionals. When he's not in the office, Dr. Mund enjoys spending time with his family, driving his classic cars and giving back to the community through various charitable initiatives."
    },
    {
      name: 'Dr. Olena Razo',
      title: 'D.D.S.',
      bio: 'Dr. Olena Razo graduated from the University of Texas Dental School at Houston. Born in Ukraine, she moved to Fort Worth at age 8.',
      image: '/images/team/dr-razo.jpg',
      specialties: ['Invisalign', 'Cosmetic Dentistry', 'Preventive Care', 'First Fit Veneers'],
      fullBio: "Dr. Olena Razo graduated from the University of Texas Dental School at Houston. Born in Ukraine, she moved to Fort Worth when she was 8 years old and has called Fort Worth home ever since. She is proud to serve the community that welcomed her family. Dr. Razo is committed to providing gentle, comprehensive dental care to patients of all ages. Her approach combines modern techniques with a warm, caring chairside manner that helps even the most anxious patients feel at ease. She has a special interest in cosmetic dentistry and Invisalign, and enjoys helping patients achieve beautiful, healthy smiles. Outside of dentistry, Dr. Razo enjoys traveling, trying new restaurants, and spending time with her family and friends. She is fluent in both English and Russian."
    },
    {
      name: 'Dr. Omar Hiwiadi',
      title: 'D.M.D.',
      bio: "Dr. Omar Hiwiadi was born and raised in Fort Worth. He specializes in cosmetic dentistry and advanced implantology.",
      image: '/images/team/dr-omar.jpg',
      specialties: ['Cosmetic Dentistry', 'Dental Implants', 'All-on-X Implants', 'Restorative Dentistry', 'First Fit Veneers'],
      fullBio: `<p>Hello, I'm Dr. Omar Hiwiadi! I was born and raised right here in Fort Worth, and my approach to dentistry is built on a simple philosophy: treating people like family and truly listening to their needs. I believe in working hand-in-hand with each person to create a treatment plan that fits their unique needs and comfort level.</p>

<p>I specialize in cosmetic dentistry and advanced implantology. Through extensive training with top specialists across the country, I focus on providing <strong>All-on-X "teeth in a day"</strong> procedures, <strong>FirstFit® cosmetic guided veneers</strong>, <strong>digital same-day implant tooth replacement</strong>, and <strong>3-D printed digital dentistry</strong>. My approach is all about collaboration, whether we are utilizing state-of-the-art digital technology for a single tooth or completing a full smile transformation, I'll take the time to make sure you're informed and comfortable every step of the way.</p>

<p>I studied at the University of North Texas, where I earned my Bachelor of Arts in Biology. I then trained at the Midwestern University College of Dental Medicine in Chicago, IL, where I earned my dental degree. I spent a few years practicing in Chicago, which is also where I met my wife, who's a dentist, too!</p>

<p>When I'm not at the office, you can usually find me hiking, exploring new cities, or just hanging out with family and friends.</p>

<p>I'm thrilled to be serving my hometown community of Fort Worth again and look forward to helping you achieve the smile you deserve!</p>`
    },
    {
      name: 'Dr. Joshua Pham',
      title: 'D.D.S.',
      bio: "Dr. Joshua Pham has resided in the DFW area for over 35 years. He earned his Doctor of Dental Surgery from the Baylor College of Dentistry in Dallas in 2011.",
      image: '/images/team/dr-pham.jpg',
      specialties: ['LANAP® Laser Gum Treatment', 'General Dentistry', 'Family Dentistry', 'First Fit Veneers'],
      fullBio: "Dr. Joshua Pham has resided in the DFW area for over 35 years. He earned his Doctor of Dental Surgery from the Baylor College of Dentistry in Dallas in 2011. Dr. Pham regularly attends continuing education courses to stay current with the latest techniques and advancements in dentistry, ensuring his patients receive modern, high-quality care. He is also LANAP certified (Laser-Assisted New Attachment Procedure) which is an FDA-cleared, minimally invasive laser treatment for moderate-to-severe gum disease. It promotes regeneration of bone and tissue, offering less pain, faster healing and minimal gum recession.\n\nDr. Pham takes pride in helping patients of all ages feel comfortable, heard and confident in their dental care.\n\nDr. Pham believes great dentistry starts with listening. He takes time to understand each patient's concerns and goals, working together to create treatment plans that support both oral health and overall well-being.\n\nOutside the office, Dr. Pham enjoys traveling, he also loves singing, music and the arts. As an active member of the community, he values connecting with people and giving back to the area he has proudly called home for decades."
    }
  ];

  const handleReadMore = (doctor) => {
    setSelectedDoctor(doctor);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDoctor(null);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // FAQ Schema for homepage - Voice Search Optimized
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who are the top dentists in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental, led by Dr. Mund and Dr. Razo, is home to the top dentists in Fort Worth. Dr. Mund is a Diplomate of the American Board of Oral Implantology, making him one of the most qualified implant dentists in Texas. With 1,100+ 5-star reviews and 35 years serving Fort Worth, we are consistently rated among the top dentists in the area."
        }
      },
      {
        "@type": "Question",
        "name": "What services do the top dentists at Fort Worth Dental offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers comprehensive dental services including dental implants, Invisalign, LANAP laser gum treatment, cosmetic dentistry, emergency dentistry, family dentistry, teeth whitening, veneers, and same-day crowns. We specialize in advanced procedures not available at most dental offices."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental accept dental insurance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Fort Worth Dental accepts most major dental insurance plans including Delta Dental, Cigna, Aetna, MetLife, Guardian, and United Healthcare. We also offer CareCredit and LendingClub financing with 0% interest options."
        }
      },
      {
        "@type": "Question",
        "name": "How much do dental implants cost in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dental implant cost depends on the number of implants, bone grafting needs, and the type of restoration — every case is unique. Fort Worth Dental provides a personalized written quote at your FREE consultation (including X-rays). Flexible 0% financing available. Call (817) 985-3337 to schedule."
        }
      },
      {
        "@type": "Question",
        "name": "Is there a dentist near me open on Saturday in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers flexible scheduling Monday through Friday, including early morning and evening appointments. While we're closed on weekends, we provide emergency dental services. Call (817) 985-3337 for same-day emergency appointments."
        }
      },
      {
        "@type": "Question",
        "name": "What is the best teeth whitening dentist near Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers professional teeth whitening services that deliver dramatically whiter teeth in just one visit. Our in-office whitening is safer and more effective than over-the-counter products. Schedule a consultation to learn about our whitening specials."
        }
      },
      {
        "@type": "Question",
        "name": "How do I find an emergency dentist in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers same-day emergency dental appointments. If you have a dental emergency like severe tooth pain, a broken tooth, or knocked-out tooth, call us immediately at (817) 985-3337. We offer $99 emergency exams."
        }
      },
      {
        "@type": "Question",
        "name": "Is Fort Worth Dental a good dentist?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Fort Worth Dental is a highly rated, locally owned dental practice in Fort Worth, TX with over 1,100 verified 5-star reviews and a 4.9-star average rating. It has served the community since 1990 and is led by Dr. Arthur Mund, a Diplomate of the American Board of Oral Implantology, and Dr. David Razo. Care is offered in both English and Spanish."
        }
      },
      {
        "@type": "Question",
        "name": "Who is the best dental implant dentist in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dr. Arthur Mund at Fort Worth Dental is one of the most qualified implant dentists in the Fort Worth area. He is a Diplomate of the American Board of Oral Implantology (ABOI) with 25+ years of experience placing single, multiple, and full-arch (All-on-4) dental implants at the practice's dedicated Implant Center."
        }
      },
      {
        "@type": "Question",
        "name": "Is Fort Worth Dental part of a corporate dental chain?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Fort Worth Dental is a locally owned, independent practice — not a corporate dental chain. Patients see the same trusted doctors at every visit, and treatment plans are based on patient needs, not corporate sales quotas."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental have a Spanish-speaking dentist?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Fort Worth Dental provides care in both English and Spanish (atención en español). Both doctors and staff can assist Spanish-speaking patients, and the website is fully available in Spanish."
        }
      }
    ]
  };

  // Speakable Schema for Voice Search (Google Assistant, Alexa, Siri)
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Fort Worth Dental - Top Dentists in Fort Worth TX",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".speakable-intro", ".speakable-services", ".speakable-contact"]
    },
    "url": "https://fortworthdental.com"
  };

  // Enhanced LocalBusiness Schema for Brand Protection & Local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": "https://fortworthdental.com/#dentist",
    "name": "Fort Worth Dental",
    "alternateName": ["Fort Dental", "Fort Worth Dentist", "FW Dental", "Top Dentists Fort Worth"],
    "description": "Fort Worth Dental is home to the top dentists in Fort Worth, TX. Established in 1990, we specialize in dental implants, Invisalign, sedation dentistry, cosmetic dentistry, and emergency dental care. Dr. Mund is a Board Certified Implant Dentist.",
    "url": "https://fortworthdental.com",
    "logo": "https://fortworthdental.com/images/logo.png",
    "image": "https://fortworthdental.com/images/building.png",
    "telephone": "(817) 985-3337",
    "email": "info@fortworthdental.com",
    "foundingDate": "1990",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.6829,
      "longitude": -97.4089
    },
    "areaServed": [
      {"@type": "City", "name": "Fort Worth", "sameAs": "https://en.wikipedia.org/wiki/Fort_Worth,_Texas"},
      {"@type": "City", "name": "Benbrook"},
      {"@type": "City", "name": "Arlington"},
      {"@type": "City", "name": "Weatherford"},
      {"@type": "City", "name": "Granbury"},
      {"@type": "City", "name": "Keller"},
      {"@type": "City", "name": "Southlake"}
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dental Services",
      "itemListElement": [
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dental Implants"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Invisalign"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Sedation Dentistry"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Cosmetic Dentistry"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Emergency Dentistry"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Teeth Whitening"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Porcelain Veneers"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "LANAP Laser Gum Treatment"}}
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1110",
      "bestRating": "5"
    },
    "openingHoursSpecification": [
      {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"], "opens": "08:00", "closes": "17:00"},
      {"@type": "OpeningHoursSpecification", "dayOfWeek": "Friday", "opens": "08:00", "closes": "15:00"}
    ],
    "sameAs": [
      "https://www.facebook.com/fortworthdental",
      "https://www.instagram.com/fortworthdental",
      "https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA"
    ],
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://fortworthdental.com/#book-appointment",
        "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
      },
      "result": {
        "@type": "Reservation",
        "name": "Dental Appointment"
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags - Optimized to outrank fortdentaltx.com */}
      <Helmet>
        <title>Fort Worth Dental | Dental Implants, Invisalign & Veneers</title>
        <meta name="description" content="Dental implants, Invisalign & cosmetic dentistry in Fort Worth, TX by board-certified Dr. Mund. 1,100+ 5-star reviews. FREE consults. Call (817) 985-3337." />
        <meta name="keywords" content="top dentists fort worth, top dentists in fort worth, fort worth dental, dentist in fort worth texas, fort worth tx dentist, best dentist fort worth texas, dental office fort worth, dentist fort worth, emergency dentist fort worth texas, sedation dentistry fort worth, invisalign fort worth, cosmetic dentistry fort worth, dental implants fort worth" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://fortworthdental.com/" />
        
        {/* Open Graph */}
        <meta property="og:url" content="https://fortworthdental.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Fort Worth Dental | Top Dentists in Fort Worth TX Since 1990" />
        <meta property="og:description" content="Top Dentists in Fort Worth. Award-winning care for implants, Invisalign & cosmetic dentistry. 1,100+ 5-star reviews. FREE consultations. Serving Fort Worth & 53 cities." />
        <meta property="og:site_name" content="Fort Worth Dental" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fort Worth Dental | Top Dentists in Fort Worth TX" />
        <meta name="twitter:description" content="Top dentists in Fort Worth for dental implants, Invisalign & emergency care. 1,100+ 5-star reviews. FREE consultations. Call (817) 985-3337." />
        
        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Fort Worth" />
        <meta name="geo.position" content="32.7357;-97.3889" />
        <meta name="ICBM" content="32.7357, -97.3889" />
        
        {/* Schema Scripts */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(speakableSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Hero Slider */}
      <section className="relative h-[400px] sm:h-[500px] lg:h-[550px] xl:h-[600px] bg-gradient-to-br from-teal-700 to-teal-900 overflow-hidden">
        {/* Hablamos Español chip — invites bilingual visitors to /es/ before they bounce.
            Absolutely positioned over all slides so it persists during the rotation. */}
        <Link
          to="/es"
          data-testid="hablamos-espanol-chip"
          aria-label="Ver sitio en Español"
          className="group absolute top-4 right-4 z-20 inline-flex items-center gap-2 bg-white/95 hover:bg-white backdrop-blur-sm text-teal-800 text-sm font-semibold px-4 py-2 rounded-full shadow-lg border border-amber-300 transition-all hover:shadow-xl hover:scale-[1.03]"
        >
          <span aria-hidden="true">🇲🇽</span>
          <span>Hablamos Español</span>
          <span aria-hidden="true" className="text-amber-600 group-hover:translate-x-0.5 transition-transform">→</span>
        </Link>
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-[1]' : 'opacity-0 pointer-events-none'
            }`}
          >
            {slide.isTeamPhoto ? (
              /* Team Photo - Special Layout with building background and team overlay */
              <div className="h-full relative">
                {/* Building background - Using WebP with fallback */}
                <picture>
                  <source srcSet="/images/building.webp" type="image/webp" />
                  <img 
                    src="/images/building.png" 
                    alt="Fort Worth Dental office building exterior at 4620 Citylake Blvd W Fort Worth TX"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    width={1920}
                    height={600}
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    style={{ objectPosition: 'center right' }}
                  />
                </picture>
                {/* Gradient overlay — neutral dark so the real building photo reads
                    naturally (not a flat teal color-wash) while keeping text legible */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to right, rgba(10, 15, 28, 0.92) 0%, rgba(10, 15, 28, 0.7) 42%, rgba(10, 15, 28, 0.28) 68%, rgba(10, 15, 28, 0) 100%)'
                  }}
                />
                
                {/* Content overlay */}
                <div className="relative h-full flex">
                  {/* Left side - Text content */}
                  <div className="w-full lg:w-1/2 h-full flex items-center">
                    <div className="px-8 lg:px-16">
                      <div className="text-white max-w-xl">
                        <div className="mb-4">
                          <span className="bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                            ⭐ Best of Fort Worth 2025
                          </span>
                        </div>
                        {index === 0 ? (
                          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{slide.title}</h1>
                        ) : (
                          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{slide.title}</h2>
                        )}
                        <p className="text-xl md:text-2xl mb-8 text-teal-100">{slide.subtitle}</p>
                        <Link
                          to={slide.link}
                          className="bg-teal-500 hover:bg-teal-400 text-white px-8 py-3 rounded-lg text-lg font-semibold inline-block relative z-10"
                        >
                          Meet The Team
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Team photo overlay */}
                  <div className="hidden lg:flex w-1/2 h-full items-end justify-center">
                    <picture>
                      <source srcSet={slide.image} type="image/webp" />
                      <img 
                        src={slide.imageFallback}
                        alt="Fort Worth Dental Team - Dr. Mund, Dr. Razo and staff"
                        className="max-h-[90%] w-auto object-contain drop-shadow-2xl"
                        width={slide.width}
                        height={slide.height}
                        loading={index === 0 ? "eager" : "lazy"}
                        fetchPriority={index === 0 ? "high" : "auto"}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            ) : (
              /* Standard slides with full background images */
              <div className="h-full relative">
                <picture>
                  <source srcSet={slide.image} type="image/webp" />
                  <img 
                    src={slide.imageFallback}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    width={slide.width}
                    height={slide.height}
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                  />
                </picture>
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to right, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.1) 100%)'
                  }}
                />
                <div className="relative container mx-auto px-4 h-full flex items-center">
                  <div className="text-white max-w-2xl pl-8">
                    {index === 0 ? (
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">{slide.title}</h1>
                    ) : (
                      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">{slide.title}</h2>
                    )}
                    <p className="text-xl md:text-2xl mb-8 drop-shadow-md">{slide.subtitle}</p>
                    <Link
                      to={slide.link}
                      className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg text-lg font-semibold inline-block shadow-lg min-h-[44px]"
                    >
                      Learn About {slide.title}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Slider Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10" role="tablist" aria-label="Hero slider navigation">
          {heroSlides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-4 h-4 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}: ${slide.title}`}
              aria-selected={index === currentSlide}
              role="tab"
            >
              <span className={`w-3 h-3 rounded-full ${index === currentSlide ? 'bg-teal-500' : 'bg-white/70'}`}></span>
            </button>
          ))}
        </div>

      </section>

      {/* Welcome Offers */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">Fort Worth&apos;s Top Dentists - Now Accepting New Patients</p>
            <h2 className="text-4xl font-bold text-gray-800">New Patient Welcome Offers</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">Experience why Fort Worth Dental is home to the top dentists in Fort Worth. Our award-winning team provides exceptional dental care for the whole family.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {welcomeOffers.map((offer, index) => (
              <ServiceCard
                key={index}
                title={offer.title}
                description={offer.description}
                image={offer.image}
                link={offer.link}
                linkText="Read More"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Two Cards - LANAP and Implants */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Link to="/lanap" className="block group">
              <div className="relative h-80 rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="/images/services/dentist-examining.jpg" 
                  alt="LANAP Laser Gum Disease Treatment - Fort Worth Dental periodontist examining patient for laser gum surgery candidacy" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <h3 className="text-white text-2xl font-bold text-center px-4">
                    Am I Good Candidate for Laser Gum Disease Treatment or LANAP®?
                  </h3>
                </div>
              </div>
            </Link>
            <Link to="/dental-implants" className="block group">
              <div className="relative h-80 rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="/images/services/dentist-examines.jpg" 
                  alt="Dental Implants Fort Worth - Dr. Mund consulting with patient about tooth replacement options and dental implant procedure" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  width={400}
                  height={320}
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <h3 className="text-white text-2xl font-bold text-center px-4">
                    Are Dental Implants right for me?
                  </h3>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Convenient Dental Services - Full Width */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <img 
                src="/images/services/frame-1293-2.png" 
                alt="Fort Worth Dental convenient appointment scheduling - Modern dental office reception area with flexible morning, afternoon and evening hours" 
                className="rounded-lg w-full"
                loading="lazy"
                width={600}
                height={400}
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Convenient Dental Services</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                Let our team work around your schedule instead of the other way around! We offer several flexible hours 
                per month for patients who have a lot to juggle in their daily lives. We offer morning, afternoon and 
                evening appointments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* We Accept Your Dental Insurance */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">We Accept Your Dental Insurance</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                We want to make paying for your dental care as simple as possible. That&apos;s why Fort Worth Dental is 
                in-network for a wide variety of dental insurance plans. Our team will be happy to explain your benefits 
                and file all paperwork on your behalf.
              </p>
            </div>
            <div>
              <img 
                src="/images/services/frame-1293.png" 
                alt="Dental insurance accepted at Fort Worth Dental - We accept most major dental insurance plans including PPO and HMO coverage" 
                className="rounded-lg w-full"
                loading="lazy"
                width={600}
                height={400}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">Meet Fort Worth Dental&apos;s Team</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Welcome to Fort Worth&apos;s Family Dentistry
            </h2>
            <p className="text-gray-600 max-w-4xl mx-auto">
              Welcome to our family-friendly, state of the art facility. Our team and doctors are dedicated to improving 
              the health and appearance of smiles from Fort Worth, Aledo, and surrounding areas all the way to Stephenville. 
              We are an all-in-one practice that offers a large variety of services such as: basic preventive care, cosmetic 
              services like teeth whitening or restorative care with dental implants, veneers, Invisalign or laser gum disease 
              treatment (LANAP).
            </p>
            <Link to="/our-team" className="inline-block mt-6 bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold">
              Meet The Team
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {doctors.map((doctor, index) => (
              <DoctorCard
                key={index}
                name={doctor.name}
                title={doctor.title}
                bio={doctor.bio}
                image={doctor.image}
                onReadMore={() => handleReadMore(doctor)}
              />
            ))}
          </div>

          {/* Team Photo */}
          <div className="mt-12 text-center">
            <img
              src="/images/team/team-photo.jpg"
              alt="Fort Worth Dental team photo - Dr. Mund, Dr. Razo, Dr. Hiwiadi, Dr. Joshua Pham and dental hygienists at our Fort Worth TX office"
              className="rounded-lg shadow-xl mx-auto max-w-4xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">What Our Patients Say</p>
            <h2 className="text-4xl font-bold text-gray-800">Patient Reviews</h2>
          </div>
          <div className="max-w-xl mx-auto">
            <GoogleReviewsWidget variant="compact" />
          </div>
          <div className="text-center mt-8">
            <Link 
              to="/testimonials" 
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              View All Testimonials
            </Link>
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <InsuranceCarousel />

      {/* Video Section - YouTube Embeds */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">See Our Practice in Action</h2>
          <p className="text-gray-600 text-center mb-8">Watch videos from Fort Worth Dental</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md" data-testid="youtube-embed-1">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  title="Fort Worth Dental Practice Video"
                  src="https://www.youtube.com/embed/videoseries?list=UUUFwAVnty9mZPxUYT9VLyIA"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800">Fort Worth Dental Videos</h3>
                <p className="text-sm text-gray-600 mt-1">Latest updates from our practice</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md" data-testid="youtube-embed-2">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  title="Dr. Arthur Mund Explains Dental Implants"
                  src="https://www.youtube.com/embed/sozRchIFTFw"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800">Dental Implants Explained</h3>
                <p className="text-sm text-gray-600 mt-1">Dr. Arthur Mund walks you through your options</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-6">
            <a 
              href="https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 font-semibold hover:text-teal-800"
            >
              Visit Our YouTube Channel &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Experience Fort Worth&apos;s Top Dentists?</h2>
          <p className="text-xl mb-8">Schedule your FREE consultation today and discover why 1,100+ patients rate us 5 stars!</p>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
            <a
              href="tel:8179853337"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block"
            >
              Call (817) 985-3337
            </a>
            <Link
              to="/#book-appointment"
              className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400 inline-block"
            >
              Book FREE Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Fort Worth Dental at a Glance — quotable, citable facts for AI assistants & patients */}
      <section className="py-16 bg-gray-50" data-testid="at-a-glance-section">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Fort Worth Dental at a Glance</h2>
          <p className="text-gray-600 text-center mb-10">Why patients across Fort Worth choose us</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { stat: '4.9★', label: '1,100+ verified 5-star reviews' },
              { stat: 'Since 1990', label: 'Locally owned — not a corporate chain' },
              { stat: 'ABOI', label: 'Board-certified implant specialist (Dr. Mund)' },
              { stat: 'EN / ES', label: 'Care in English & Spanish' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-6 text-center" data-testid={`glance-card-${i}`}>
                <div className="text-2xl font-extrabold text-teal-600 mb-1">{item.stat}</div>
                <div className="text-sm text-gray-600 leading-snug">{item.label}</div>
              </div>
            ))}
          </div>
          <p className="text-gray-700 text-center mt-8 max-w-3xl mx-auto">
            Fort Worth Dental is a locally owned, independent dental practice in Fort Worth, TX, serving
            the community since 1990. Led by <strong>Dr. Arthur Mund, DDS</strong> (Diplomate, American Board
            of Oral Implantology) and <strong>Dr. David Razo, DDS</strong>, we provide dental implants,
            All-on-4, Invisalign, veneers, LANAP laser gum treatment, cosmetic and emergency dentistry — with
            1,100+ verified 5-star reviews and bilingual care. Call{' '}
            <a href="tel:8179853337" className="text-teal-600 font-semibold hover:underline">(817) 985-3337</a>.
          </p>
        </div>
      </section>

      {/* Visible FAQ — mirrors FAQPage schema so AI assistants & voice search can extract answers */}
      <section className="py-16 bg-white" data-testid="home-faq-section">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Frequently Asked Questions</h2>
          <p className="text-gray-600 text-center mb-8">Answers to what Fort Worth patients ask most</p>
          <div className="space-y-4">
            {faqSchema.mainEntity.map((q, i) => (
              <details key={i} className="group bg-gray-50 rounded-lg p-5" data-testid={`home-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-gray-800 list-none flex justify-between items-center">
                  <span>{q.name}</span>
                  <span className="text-teal-600 ml-4 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="text-gray-600 mt-3 leading-relaxed">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Dental Tips & News Section - Internal Links to Blog */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Dental Tips & News</h2>
          <p className="text-gray-600 text-center mb-8">Expert advice from your Fort Worth dental team</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <a href="/blogs/foods-to-eat-after-teeth-whitening" className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition group">
              <span className="text-3xl mb-3 block">🦷</span>
              <h3 className="font-bold text-gray-800 mb-2 group-hover:text-teal-600">Foods to Eat After Teeth Whitening</h3>
              <p className="text-gray-600 text-sm">What to eat and avoid for lasting whitening results.</p>
            </a>
            <a href="/first-fit-veneers" className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition group">
              <span className="text-3xl mb-3 block">💎</span>
              <h3 className="font-bold text-gray-800 mb-2 group-hover:text-teal-600">First Fit Veneers - NEW!</h3>
              <p className="text-gray-600 text-sm">Get a new smile in just 2 hours with First Fit Veneers.</p>
            </a>
            <a href="/blogs/sedation-dentistry-fort-worth-anxious-patients" className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition group">
              <span className="text-3xl mb-3 block">😴</span>
              <h3 className="font-bold text-gray-800 mb-2 group-hover:text-teal-600">Sedation Dentistry Options</h3>
              <p className="text-gray-600 text-sm">Comfortable care for anxious patients.</p>
            </a>
          </div>
          <div className="text-center mt-8">
            <Link to="/blogs" className="text-teal-600 font-semibold hover:text-teal-800">
              View All Dental News & Tips →
            </Link>
          </div>
        </div>
      </section>

      {/* Office Hours & Booking Section - At Bottom Just Above Footer */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Office Hours */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Office Hours</h2>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-teal-600 text-white">
                    <tr>
                      <th className="py-4 px-6 text-left">Days</th>
                      <th className="py-4 px-6 text-left">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-6">Monday</td>
                      <td className="py-3 px-6">9:00am – 5:00pm</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-6">Tuesday</td>
                      <td className="py-3 px-6">9:00am – 6:00pm</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-6">Wednesday</td>
                      <td className="py-3 px-6">7:00am – 5:00pm</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-6">Thursday</td>
                      <td className="py-3 px-6">9:00am – 6:00pm</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-6">Friday</td>
                      <td className="py-3 px-6">7:00am – 2:00pm</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Appointment Form */}
            <div id="book-appointment">
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Bio Modal */}
      <BioModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        doctor={selectedDoctor}
      />
    </div>
  );
};

export default Home;
